import React, { useEffect, useRef } from 'react';
import DateRangePicker from './DateRangePicker';

/**
 * Reusable FilterBar Component
 *
 * A flexible filter bar that renders various filter inputs (select, text, number, etc.)
 * in a compact, responsive layout.
 *
 * @example
 * const filterConfig = [
 *   {
 *     type: 'select',
 *     name: 'status',
 *     placeholder: 'All Status',
 *     options: [
 *       { value: '', label: 'All Status' },
 *       { value: 'active', label: 'Active' },
 *       { value: 'inactive', label: 'Inactive' }
 *     ],
 *     width: 'auto'
 *   },
 *   {
 *     type: 'text',
 *     name: 'search',
 *     placeholder: 'Search...',
 *     width: '200px'
 *   }
 * ];
 *
 * <FilterBar
 *   filters={filters}
 *   filterConfig={filterConfig}
 *   onFilterChange={handleFilterChange}
 *   onSearch={handleSearch}
 *   onClear={handleClear}
 *   loading={loading}
 * />
 */
const VCXEventFilterBar = ({
  filters = {},
  appliedFilters = {},
  filterConfig = [],
  onFilterChange,
  onSearch,
  onClear,
  onSaveAsDefault,
  loading = false,
  searchButtonText = 'Search',
  clearButtonText = '',
  showClearButton = true,
  showSaveButton = false,
  showAssignedToMe = false,
  currentUserId = null,
  containerClassName = '',
  containerStyle = {}
}) => {
  const shouldAutoSubmitRef = useRef(false);

  const handleChange = (name, value) => {
    if (onFilterChange) {
      onFilterChange(name, value);
    }
  };

  // Auto-submit when assigned_to filter changes (from button clicks)
  useEffect(() => {
    if (shouldAutoSubmitRef.current && onSearch) {
      shouldAutoSubmitRef.current = false;
      onSearch();
    }
  }, [filters.assigned_to, onSearch]);

  const handleBlur = (name, onBlurCallback) => {
    if (onBlurCallback) {
      onBlurCallback(filters[name]);
    }
  };

  // Helper to format date string without timezone issues
  const formatDateString = (dateStr) => {
    const [year, month, day] = dateStr.split('-');
    return new Date(year, month - 1, day).toLocaleDateString();
  };

  const buildFilterSummary = () => {
    const parts = [];
    const activeFilters = Object.keys(appliedFilters).length > 0 ? appliedFilters : filters;

    // 1. Status (Active/Archived)
    const statusText = activeFilters.status === 'archive' ? 'archived' : (activeFilters.status || 'active');
    let baseText = `Showing ${statusText} events`;

    // 2. Outcome
    if (activeFilters.outcome) {
      const outcomes = activeFilters.outcome.split(',').filter(o => o.trim());
      if (outcomes.length > 0) {
        const outcomeLabels = outcomes.map(o => {
          switch(o.trim()) {
            case 'new': return 'new';
            case 'in_progress': return 'in progress';
            case 'resolved': return 'resolved';
            default: return o;
          }
        });
        baseText += ` with ${outcomeLabels.join(' or ')} outcome`;
      }
    }
    parts.push(baseText);

    // 3. Assigned to
    if (activeFilters.assigned_to) {
      if (activeFilters.assigned_to === 'unassigned') {
        parts.push(`unassigned`);
      } else if (activeFilters.assigned_to === 'any_assigned') {
        parts.push(`assigned to anyone`);
      } else {
        parts.push(`assigned to me`);
      }
    }

    // 4. Channel
    if (activeFilters.channel) {
      parts.push(`via ${activeFilters.channel} channel`);
    }

    // 5. Direction
    if (activeFilters.direction) {
      parts.push(`${activeFilters.direction} calls`);
    }

    // 6. Customer type (Caller Type)
    if (activeFilters.customer_type) {
      const customerLabels = {
        patient: 'patients',
        family: 'family members',
        vendor: 'vendors',
        insurance: 'insurance',
        other_provider: 'other providers'
      };
      parts.push(`from ${customerLabels[activeFilters.customer_type] || activeFilters.customer_type}`);
    }

    // 7. Call domain (Domain)
    if (activeFilters.call_domain) {
      const domainLabels = {
        appointments: 'appointments',
        medications: 'medications',
        medicalIssues: 'medical issues',
        billingInsurance: 'billing/insurance',
        medicalRecords: 'medical records',
        new_patient: 'new patients',
        generalInquiries: 'general inquiries',
        providerCalls: 'provider calls',
        vendorCalls: 'vendor calls',
        unknown: 'unknown'
      };
      parts.push(`about ${domainLabels[activeFilters.call_domain] || activeFilters.call_domain}`);
    }

    // 8. Priority
    if (activeFilters.priority) {
      parts.push(`with ${activeFilters.priority} priority`);
    }

    // 9. Follow-up required
    if (activeFilters.follow_up_required === 'true') {
      parts.push(`requiring follow-up`);
    }

    // 10. Date range
    if (activeFilters.from_date || activeFilters.to_date) {
      if (activeFilters.from_date && activeFilters.to_date) {
        // If both dates are the same, just show "from [date]"
        if (activeFilters.from_date === activeFilters.to_date) {
          parts.push(`from ${formatDateString(activeFilters.from_date)}`);
        } else {
          parts.push(`from ${formatDateString(activeFilters.from_date)} to ${formatDateString(activeFilters.to_date)}`);
        }
      } else if (activeFilters.from_date) {
        parts.push(`from ${formatDateString(activeFilters.from_date)} onwards`);
      } else {
        parts.push(`until ${formatDateString(activeFilters.to_date)}`);
      }
    }

    // 11. Search
    if (activeFilters.search) {
      parts.push(`matching "${activeFilters.search}"`);
    }

    return parts.join(', ') + '.';
  };

  const activeFilters = Object.keys(appliedFilters).length > 0 ? appliedFilters : filters;
  const hasActiveFilters = activeFilters.assigned_to || activeFilters.channel || activeFilters.direction || activeFilters.call_domain ||
                          activeFilters.priority || activeFilters.customer_type || activeFilters.follow_up_required ||
                          activeFilters.from_date || activeFilters.to_date || activeFilters.search || activeFilters.outcome;

  const renderFilter = (config, index) => {
    const {
      type,
      name,
      placeholder,
      options = [],
      width = 'auto',
      style = {},
      onBlur: onBlurCallback,
      title,
      disabled = false,
      min,
      max,
      step
    } = config;

    const key = `filter-${name}-${index}`;
    const commonProps = {
      value: filters[name] || '',
      onChange: (e) => handleChange(name, e.target.value),
      disabled: disabled || loading,
      style: { width, ...style },
      title
    };

    switch (type) {
      case 'select':
        return (
          <select
            key={key}
            {...commonProps}
            className="form-select form-select-sm"
          >
            {options.map((option, idx) => (
              <option
                key={`${name}-option-${idx}`}
                value={option.value}
                title={option.title}
              >
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'text':
        return (
          <input
            key={key}
            {...commonProps}
            type="text"
            className="form-control form-control-sm"
            placeholder={placeholder}
            onBlur={() => handleBlur(name, onBlurCallback)}
          />
        );

      case 'number':
        return (
          <input
            key={key}
            {...commonProps}
            type="number"
            className="form-control form-control-sm"
            placeholder={placeholder}
            min={min}
            max={max}
            step={step}
            onBlur={() => handleBlur(name, onBlurCallback)}
          />
        );

      case 'date':
        return (
          <input
            key={key}
            {...commonProps}
            type="date"
            className="form-control form-control-sm"
            onBlur={() => handleBlur(name, onBlurCallback)}
          />
        );

      case 'datetime-local':
        return (
          <input
            key={key}
            {...commonProps}
            type="datetime-local"
            className="form-control form-control-sm"
            onBlur={() => handleBlur(name, onBlurCallback)}
          />
        );

      case 'daterange':
        return (
          <DateRangePicker
            key={key}
            fromDate={filters.from_date || ''}
            toDate={filters.to_date || ''}
            onFromDateChange={(value) => handleChange('from_date', value)}
            onToDateChange={(value) => handleChange('to_date', value)}
            disabled={disabled || loading}
            placeholder={placeholder}
            width={width}
            style={style}
          />
        );

      default:
        return null;
    }
  };

  return (
      <div className={` ${containerClassName}`} style={containerStyle}>
        <div className="d-flex align-items-center gap-2 flex-wrap mb-2 p-3">
          {/* Assigned to Me/Anyone/Unassigned Buttons */}
          {showAssignedToMe && currentUserId && (
            <>
              <span className="text-muted fw-bold">Assigned to:</span>
              <div className="btn-group" role="group">
                <button
                  type="button"
                  className={`btn btn-sm ${filters.assigned_to === currentUserId ? 'btn-dark' : 'btn-outline-dark'}`}
                  onClick={() => {
                    shouldAutoSubmitRef.current = true;
                    // Toggle off if already active
                    handleChange('assigned_to', filters.assigned_to === currentUserId ? '' : currentUserId);
                  }}
                  disabled={loading}
                  title="Show events assigned to me (click again to clear filter)"
                ><i className="fas fa-user-check me-1"></i> Me
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${filters.assigned_to === 'any_assigned' ? 'btn-dark' : 'btn-outline-dark'}`}
                  onClick={() => {
                    shouldAutoSubmitRef.current = true;
                    // Toggle off if already active
                    handleChange('assigned_to', filters.assigned_to === 'any_assigned' ? '' : 'any_assigned');
                  }}
                  disabled={loading}
                  title="Show events assigned to anyone (click again to clear filter)"
                >Anyone
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${filters.assigned_to === 'unassigned' ? 'btn-dark' : 'btn-outline-dark'}`}
                  onClick={() => {
                    shouldAutoSubmitRef.current = true;
                    // Toggle off if already active
                    handleChange('assigned_to', filters.assigned_to === 'unassigned' ? '' : 'unassigned');
                  }}
                  disabled={loading}
                  title="Show unassigned events (click again to clear filter)"
                >Unassigned
                </button>
              </div>
            </>
          )}

          {/* Render all filter inputs */}
          {filterConfig.map((config, index) => renderFilter(config, index))}

          {/* Search Button */}
          <button
            className="btn btn-sm btn-primary"
            onClick={onSearch}
            disabled={loading}
          >
            <i className="fas fa-search me-1"></i>
            {loading ? 'Searching...' : searchButtonText}
          </button>

          {/* Save as Default Button */}
          {showSaveButton && onSaveAsDefault && filters.hasChanges && (
            <button
              className="btn btn-sm btn-success"
              onClick={onSaveAsDefault}
              disabled={loading || !filters.hasChanges}
              title={filters.hasChanges ? "Save current filter settings as your default preferences" : "No changes to save"}
            >
              <i className="fa fa-save"></i>
            </button>
          )}

          {/* Clear Button */}
          {showClearButton && (
            <button
              className="btn btn-sm btn-secondary"
              onClick={onClear}
              disabled={loading}
              title="Clear all filters"
            >
              {clearButtonText ? (
                <>
                  <i className="fas fa-times me-1"></i>
                  {clearButtonText}
                </>
              ) : (
                <i className="fas fa-times"></i>
              )}
            </button>
          )}
        </div>

        {/* Filter Summary */}
        {hasActiveFilters && (
          <div className="text-xs text-primary px-3 mb-2">
                <i className="fas fa-filter me-2"></i>
                {buildFilterSummary()}
          </div>
        )}
      </div>
  );
};

export default VCXEventFilterBar;
